import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { Video } from '../lib/types';
import { VideoCard } from '../components/VideoCard';
import { Thumb } from '../components/Thumb';
import { FilterPills } from '../components/FilterPills';

const SORTS = ['新しい順', '視聴回数の多い順'] as const;
type Sort = (typeof SORTS)[number];

function featureLabel(positionSec: number, durationSec: number | undefined): string {
  const watched = positionSec > 0 && !!durationSec && positionSec >= durationSec - 5;
  if (watched) return '▶ もう一度見る';
  if (positionSec > 5) return '▶ 続きから見る';
  return '▶ 見る';
}

export function Home(): JSX.Element {
  const [videos, setVideos] = useState<Video[]>([]);
  const [later, setLater] = useState(false);
  const [sort, setSort] = useState<Sort>('新しい順');
  const [featuredResumeSec, setFeaturedResumeSec] = useState<number>(0);
  const nav = useNavigate();

  useEffect(() => {
    api.listVideos().then(setVideos).catch(() => setVideos([]));
  }, []);

  const featured = videos[0];

  useEffect(() => {
    if (!featured) return;
    setLater(false);
    Promise.all([
      api.getProgress(featured.videoId).catch(() => ({ positionSec: 0 } as const)),
      api.listLaterVideoIds().catch(() => [] as string[]),
    ]).then(([p, laterIds]) => {
      setFeaturedResumeSec(p.positionSec ?? 0);
      setLater(laterIds.includes(featured.videoId));
    });
  }, [featured?.videoId]);
  const sortedGrid = useMemo(() => {
    if (sort === '視聴回数の多い順') {
      return [...videos].sort((a, b) => (b.views ?? 0) - (a.views ?? 0));
    }
    return videos;
  }, [videos, sort]);

  return (
    <div style={{ padding: '28px 30px 60px' }}>
      {featured && (
        <div
          style={{
            display: 'flex',
            gap: 22,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 18,
            padding: 22,
            alignItems: 'center',
            marginBottom: 30,
            flexWrap: 'wrap',
          }}
        >
          <Thumb
            video={featured}
            onClick={() => nav(`/videos/${featured.videoId}`)}
            style={{ width: 300, flex: '1 1 260px', maxWidth: 380 }}
          />
          <div style={{ minWidth: 260, flex: '2 1 320px' }}>
            <div className="pill" style={{ marginBottom: 12 }}>
              新着 · {featured.channelName}
            </div>
            <h1
              onClick={() => nav(`/videos/${featured.videoId}`)}
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: 26,
                margin: '0 0 8px',
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
              }}
            >
              {featured.title}
            </h1>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, color: 'var(--brand-tint)', lineHeight: 1.75 }}>
              {featured.description}
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => nav(`/videos/${featured.videoId}`)}>
                {featureLabel(featuredResumeSec, featured.durationSec)}
              </button>
              <button
                className="btn-ghost"
                onClick={() => {
                  const next = !later;
                  setLater(next);
                  void api.toggleLater(featured.videoId, next);
                }}
              >
                {later ? '✓ あとで見るに追加済み' : '＋ あとで見る'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <FilterPills options={[...SORTS]} value={sort} onChange={(v) => setSort(v as Sort)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 20 }}>
        {sortedGrid.map((v) => (
          <VideoCard key={v.videoId} video={v} />
        ))}
      </div>
    </div>
  );
}
